import { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion, useMotionValue, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  ScanLine,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Building2,
  Receipt as ReceiptIcon,
  DollarSign,
  Calendar,
  Percent,
  Lock,
  Fingerprint,
  Vault,
  CreditCard,
  Droplet,
  Terminal,
  FileText,
  Globe,
  FolderArchive,
  AlertTriangle,
} from "lucide-react";

// Ultra-smooth easing curve applied everywhere
const EASE = [0.4, 0, 0.2, 1];

/* ============================ HERO ============================ */
function HeroStage() {
  const cardRef = useRef(null);
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const springX = useSpring(tiltX, { stiffness: 120, damping: 20 });
  const springY = useSpring(tiltY, { stiffness: 120, damping: 20 });

  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    tiltY.set(((e.clientX - cx) / r.width) * 16);
    tiltX.set(((cy - e.clientY) / r.height) * 16);
  };
  const reset = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section
      className="relative h-[100vh] flex items-center justify-center overflow-hidden"
      data-testid="home-hero"
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{
        background:
          "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.14), transparent 50%), radial-gradient(circle at 80% 90%, rgba(16,185,129,0.09), transparent 55%), #0f172a",
      }}
    >
      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(circle at center, black 40%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(circle at center, black 40%, transparent 85%)",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-8 grid lg:grid-cols-2 gap-12 items-center w-full">
        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-6">
            <Sparkles size={12} /> Now with Sheet AI
          </div>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-white"
            data-testid="home-hero-heading"
          >
            The receipt vault<br />
            <span className="text-emerald-400">that thinks in dollars.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
            Snap, forward, or scan. GPT-5.2 Vision extracts every line item,
            flags every deduction, and archives it in an audit-proof vault —
            all before you close the app.
          </p>
          <div className="mt-10 flex items-center gap-4">
            <button
              onClick={() => (window.location.href = "/paywall")}
              data-testid="home-cta-primary"
              className="btn-neon h-14 px-8 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base flex items-center gap-2"
            >
              Start scanning — from $9.99/mo <ArrowRight size={18} />
            </button>
            <button
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
              className="btn-neon-ghost h-14 px-6 rounded-xl border border-white/15 text-white text-sm font-medium hover:bg-white/5"
            >
              See how it works
            </button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-slate-500">
            <span className="flex items-center gap-1.5">
              <Lock size={12} /> SOC 2
            </span>
            <span>·</span>
            <span>256-bit AES</span>
            <span>·</span>
            <span>GDPR</span>
          </div>
        </motion.div>

        {/* 3D floating receipt card */}
        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 40, rotate: -4 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.15 }}
          style={{
            rotateX: springX,
            rotateY: springY,
            transformStyle: "preserve-3d",
            perspective: 1200,
          }}
          className="relative mx-auto w-[340px] sm:w-[380px]"
          data-testid="home-hero-card"
        >
          {/* Glow halo */}
          <div
            className="absolute inset-0 -z-10 blur-3xl"
            style={{
              background:
                "radial-gradient(circle at center, rgba(16,185,129,0.55), transparent 60%)",
            }}
          />
          <div
            className="rounded-3xl p-6 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(150deg, rgba(15,23,42,0.85), rgba(15,23,42,0.6))",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(16,185,129,0.35)",
              boxShadow:
                "0 30px 80px -20px rgba(16,185,129,0.5), 0 0 0 1px rgba(16,185,129,0.2), inset 0 0 60px rgba(16,185,129,0.04)",
            }}
          >
            {/* Receipt header */}
            <div className="flex items-center justify-between pb-4 border-b border-dashed border-white/10">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
                  Receipt · verified
                </div>
                <div className="mt-1 text-lg font-bold text-white tracking-tight">
                  Marriott Downtown
                </div>
              </div>
              <ReceiptIcon size={26} className="text-emerald-400" />
            </div>
            {/* Rows */}
            <div className="mt-4 space-y-3 text-sm">
              {[
                ["Date", "Feb 14, 2026"],
                ["Room · 1 night", "$289.00"],
                ["Valet parking", "$45.00"],
                ["Room service", "$36.05"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-slate-400">{k}</span>
                  <span className="text-white font-mono">{v}</span>
                </div>
              ))}
              <div className="border-t border-dashed border-white/10 pt-3 flex justify-between text-slate-400">
                <span>Sales tax</span>
                <span className="font-mono">$42.85</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-slate-300 font-semibold uppercase tracking-widest text-xs">
                  Total
                </span>
                <span className="font-mono text-2xl font-bold text-emerald-400">
                  $412.90
                </span>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                <ShieldCheck size={10} /> Deductible
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                #RS-84421
              </span>
            </div>

            {/* Corner brackets */}
            {["top-3 left-3 border-t-2 border-l-2 rounded-tl-lg", "top-3 right-3 border-t-2 border-r-2 rounded-tr-lg", "bottom-3 left-3 border-b-2 border-l-2 rounded-bl-lg", "bottom-3 right-3 border-b-2 border-r-2 rounded-br-lg"].map((c, i) => (
              <span
                key={i}
                className={`absolute w-6 h-6 border-emerald-400/70 pointer-events-none ${c}`}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] font-mono text-slate-500 flex flex-col items-center gap-2"
      >
        <span>Scroll</span>
        <span className="w-px h-8 bg-gradient-to-b from-emerald-400 to-transparent" />
      </motion.div>
    </section>
  );
}

/* ============================ SCAN ============================ */
function ScanStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const laserY = useTransform(scrollYProgress, [0.2, 0.8], ["-8%", "108%"]);
  const laserOpacity = useTransform(scrollYProgress, [0.15, 0.25, 0.75, 0.85], [0, 1, 1, 0]);
  const cardTilt = useTransform(scrollYProgress, [0, 1], [12, -12]);

  // Pre-hoist tag transforms (must be called unconditionally, not inside .map)
  const t0o = useTransform(scrollYProgress, [0.30, 0.38], [0, 1]);
  const t0x = useTransform(scrollYProgress, [0.30, 0.42], [-30, 0]);
  const t1o = useTransform(scrollYProgress, [0.42, 0.50], [0, 1]);
  const t1x = useTransform(scrollYProgress, [0.42, 0.54], [-30, 0]);
  const t2o = useTransform(scrollYProgress, [0.54, 0.62], [0, 1]);
  const t2x = useTransform(scrollYProgress, [0.54, 0.66], [-30, 0]);
  const t3o = useTransform(scrollYProgress, [0.66, 0.74], [0, 1]);
  const t3x = useTransform(scrollYProgress, [0.66, 0.78], [-30, 0]);

  const TAGS = [
    { icon: DollarSign, label: "Total", value: "$412.90", o: t0o, x: t0x },
    { icon: Calendar, label: "Date", value: "2026-02-14", o: t1o, x: t1x },
    { icon: Percent, label: "Sales tax", value: "$42.85", o: t2o, x: t2x },
    { icon: ShieldCheck, label: "Category", value: "Travel · Deductible", o: t3o, x: t3x },
  ];

  return (
    <section
      ref={ref}
      className="relative min-h-[180vh]"
      style={{ background: "#0f172a" }}
      data-testid="home-scan"
    >
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="max-w-6xl mx-auto px-8 grid lg:grid-cols-2 gap-16 items-center w-full">
          {/* Left: card with laser */}
          <motion.div
            style={{ rotateY: cardTilt, transformStyle: "preserve-3d", perspective: 1400 }}
            className="relative mx-auto w-[320px] sm:w-[360px]"
          >
            <div
              className="rounded-3xl p-6 relative overflow-hidden"
              style={{
                background: "linear-gradient(150deg, rgba(15,23,42,0.85), rgba(15,23,42,0.6))",
                border: "1px solid rgba(16,185,129,0.35)",
                boxShadow: "0 30px 80px -20px rgba(16,185,129,0.5)",
                minHeight: 380,
              }}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
                Analyzing…
              </div>
              <div className="mt-1 text-lg font-bold text-white tracking-tight">
                Marriott Downtown
              </div>
              <div className="mt-6 space-y-3 opacity-60 text-sm">
                {[
                  ["Line 01", "$289.00"],
                  ["Line 02", "$45.00"],
                  ["Line 03", "$36.05"],
                  ["Subtotal", "$370.05"],
                  ["Tax 10.4%", "$42.85"],
                  ["TOTAL", "$412.90"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono">
                    <span className="text-slate-400">{k}</span>
                    <span className="text-white">{v}</span>
                  </div>
                ))}
              </div>

              {/* Neon laser bar */}
              <motion.div
                style={{
                  top: laserY,
                  opacity: laserOpacity,
                  boxShadow:
                    "0 0 14px rgba(16,185,129,1), 0 0 40px rgba(16,185,129,0.75)",
                }}
                className="absolute left-0 right-0 h-[3px] bg-emerald-400"
              />
              {/* Scan tint */}
              <motion.div
                style={{ opacity: laserOpacity }}
                className="absolute inset-0 pointer-events-none"
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, rgba(16,185,129,0.09), transparent 70%)",
                  }}
                />
              </motion.div>
            </div>
          </motion.div>

          {/* Right: dashboard matrix */}
          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3">
              Stage 02 · Extraction
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              A laser reads every line.<br />
              <span className="text-emerald-400">The dashboard writes itself.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
              Watch merchant, date, sales tax and total translate straight from
              crinkled thermal print into a structured financial matrix — with
              deduction flags and FX conversion applied in the same beat.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {TAGS.map((t) => (
                <motion.div
                  key={t.label}
                  style={{ opacity: t.o, x: t.x }}
                  className="rounded-xl p-4 bg-white/[0.03] border border-white/5"
                >
                  <div className="flex items-center gap-2 text-slate-400 text-[10px] uppercase tracking-widest">
                    <t.icon size={12} className="text-emerald-400" />
                    {t.label}
                  </div>
                  <div className="mt-1.5 font-mono text-white font-bold text-lg">
                    {t.value}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ VAULT ============================ */
function VaultStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 1, 1.05]);
  const z = useTransform(scrollYProgress, [0, 0.5], [-500, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.85, 1], [0, 1, 1, 0.7]);
  const blur = useTransform(scrollYProgress, [0, 0.35], [10, 0]);
  const filter = useTransform(blur, (v) => `blur(${v}px)`);

  // Pre-hoist per-row transforms
  const r0y = useTransform(scrollYProgress, [0.25, 0.35], [40, 0]);
  const r0o = useTransform(scrollYProgress, [0.25, 0.35], [0, 1]);
  const r1y = useTransform(scrollYProgress, [0.31, 0.41], [40, 0]);
  const r1o = useTransform(scrollYProgress, [0.31, 0.41], [0, 1]);
  const r2y = useTransform(scrollYProgress, [0.37, 0.47], [40, 0]);
  const r2o = useTransform(scrollYProgress, [0.37, 0.47], [0, 1]);
  const r3y = useTransform(scrollYProgress, [0.43, 0.53], [40, 0]);
  const r3o = useTransform(scrollYProgress, [0.43, 0.53], [0, 1]);
  const r4y = useTransform(scrollYProgress, [0.49, 0.59], [40, 0]);
  const r4o = useTransform(scrollYProgress, [0.49, 0.59], [0, 1]);
  const r5y = useTransform(scrollYProgress, [0.55, 0.65], [40, 0]);
  const r5o = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);

  const ROWS = [
    { m: "Adobe Creative Cloud", d: "2026-02-10", t: "$54.99", tag: "Software", y: r0y, o: r0o },
    { m: "Marriott Downtown", d: "2026-02-14", t: "$412.90", tag: "Travel · Deductible", y: r1y, o: r1o },
    { m: "Home Depot", d: "2026-02-11", t: "$137.22", tag: "Office · Deductible", y: r2y, o: r2o },
    { m: "Chipotle Mexican", d: "2026-02-08", t: "$24.15", tag: "Meals", y: r3y, o: r3o },
    { m: "Shell Gas Station", d: "2026-02-07", t: "$52.30", tag: "Transportation", y: r4y, o: r4o },
    { m: "Amazon.com", d: "2026-02-04", t: "$89.99", tag: "Office · Deductible", y: r5y, o: r5o },
  ];

  return (
    <section
      ref={ref}
      className="relative min-h-[180vh]"
      style={{ background: "#0f172a" }}
      data-testid="home-vault"
    >
      <div className="sticky top-0 h-screen flex items-center overflow-hidden" style={{ perspective: 1600 }}>
        <div className="max-w-6xl mx-auto px-8 w-full">
          <div className="text-center mb-10">
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3">
              Stage 03 · Archive
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Every dollar filed in the{" "}
              <span className="text-emerald-400">Tax & Audit Vault.</span>
            </h2>
          </div>

          <motion.div
            style={{ scale, translateZ: z, opacity, filter, transformStyle: "preserve-3d" }}
            className="mx-auto max-w-3xl rounded-3xl overflow-hidden"
          >
            <div
              className="relative"
              style={{
                background:
                  "linear-gradient(160deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: "1px solid rgba(16,185,129,0.4)",
                boxShadow:
                  "0 40px 100px -20px rgba(16,185,129,0.5), inset 0 0 60px rgba(16,185,129,0.04)",
              }}
            >
              {/* Vault header */}
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
                    <Vault size={18} className="text-emerald-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white tracking-tight">
                      Tax & Audit Vault
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                      ● IRS-compliant · certified
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    YTD Deductible
                  </div>
                  <div className="font-mono text-2xl font-bold text-emerald-400">
                    $2,847.13
                  </div>
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-white/5">
                {ROWS.map((r, i) => (
                  <motion.div
                    key={i}
                    style={{ y: r.y, opacity: r.o }}
                    className="px-6 py-3.5 flex items-center gap-4"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center shrink-0">
                      <ReceiptIcon size={15} className="text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{r.m}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-2">
                        <span>{r.d}</span>
                        <span>·</span>
                        <span>{r.tag}</span>
                      </div>
                    </div>
                    <div className="font-mono font-bold text-white">{r.t}</div>
                  </motion.div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-slate-400">
                  <Fingerprint size={12} className="text-emerald-400" />
                  Hash-verified · 2,411 receipts
                </div>
                <button
                  onClick={() => (window.location.href = "/paywall")}
                  data-testid="home-vault-cta"
                  className="btn-neon h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2"
                >
                  Unlock the Vault <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ============================ LEAK ============================ */
function LeakStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const cardY = useTransform(scrollYProgress, [0, 0.4], [40, -10]);
  const cardTilt = useTransform(scrollYProgress, [0, 1], [-4, 4]);

  const P_COUNT = 12;
  const py = [], pc = [], psc = [];
  for (let i = 0; i < P_COUNT; i++) {
    const start = 0.05 + (i % 6) * 0.04;
    const shield = 0.5;
    const end = 0.85;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    py.push(useTransform(scrollYProgress, [start, end], ["-20px", "360px"]));
    // eslint-disable-next-line react-hooks/rules-of-hooks
    pc.push(useTransform(scrollYProgress, [start, shield - 0.02, shield + 0.02, end], ["#ef4444", "#f87171", "#10b981", "#10b981"]));
    // eslint-disable-next-line react-hooks/rules-of-hooks
    psc.push(useTransform(scrollYProgress, [start, shield, shield + 0.05, end], [1, 1.3, 0.9, 1]));
  }

  const B_COUNT = 8;
  const blockOp = [], blockY = [];
  for (let i = 0; i < B_COUNT; i++) {
    const s = 0.5 + i * 0.03;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    blockOp.push(useTransform(scrollYProgress, [s, s + 0.08], [0, 1]));
    // eslint-disable-next-line react-hooks/rules-of-hooks
    blockY.push(useTransform(scrollYProgress, [s, s + 0.1], [20, 0]));
  }

  return (
    <section ref={ref} className="relative min-h-[180vh]" style={{ background: "#0f172a" }} data-testid="home-leak">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="max-w-6xl mx-auto px-8 grid lg:grid-cols-2 gap-16 items-center w-full">
          <div className="relative mx-auto w-[380px] h-[500px]">
            <motion.div
              style={{
                y: cardY, rotateZ: cardTilt, transformStyle: "preserve-3d", willChange: "transform",
                background: "linear-gradient(135deg, #0b1220 0%, #1a2338 60%, #0b1220 100%)",
                boxShadow: "0 20px 60px -20px rgba(239,68,68,0.35), 0 0 0 1px rgba(239,68,68,0.25)",
              }}
              className="absolute top-4 left-1/2 -translate-x-1/2 w-64 h-40 rounded-2xl p-4 border border-red-500/25"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-red-300">Recurring charge</div>
                <CreditCard size={18} className="text-red-300" />
              </div>
              <div className="mt-8 font-mono text-lg tracking-widest text-white">•••• •••• •••• 4242</div>
              <div className="mt-2 flex justify-between text-xs font-mono text-slate-400">
                <span>UNUSED SaaS · 8 mo</span>
                <span>$29.99/mo</span>
              </div>
            </motion.div>

            {Array.from({ length: P_COUNT }).map((_, i) => {
              const left = 30 + ((i * 47) % 260);
              return (
                <motion.span
                  key={i}
                  data-testid={`leak-particle-${i}`}
                  style={{
                    left: `${left}px`, y: py[i], scale: psc[i], background: pc[i],
                    boxShadow: "0 0 12px currentColor", willChange: "transform",
                  }}
                  className="absolute top-40 w-3 h-3 rounded-sm block"
                />
              );
            })}

            <div className="absolute left-0 right-0 top-[300px] h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(16,185,129,0.9), transparent)", boxShadow: "0 0 20px rgba(16,185,129,0.55)" }} />
            <div className="absolute top-[288px] left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-slate-950/80 border border-emerald-500/40 text-[10px] font-mono uppercase tracking-widest text-emerald-300">
              ● Freeze Shield
            </div>

            <div className="absolute bottom-0 left-0 right-0 grid grid-cols-4 gap-2 p-2">
              {Array.from({ length: B_COUNT }).map((_, i) => (
                <motion.div
                  key={i}
                  data-testid={`leak-frozen-${i}`}
                  style={{
                    opacity: blockOp[i], y: blockY[i],
                    background: "rgba(16,185,129,0.15)",
                    boxShadow: "inset 0 0 12px rgba(16,185,129,0.35), 0 0 12px rgba(16,185,129,0.2)",
                    willChange: "transform",
                  }}
                  className="h-10 rounded-md border border-emerald-500/40 flex items-center justify-center font-mono text-[10px] text-emerald-200"
                >
                  $29
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3">Stage 04 · Leak Detector</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Recurring charges freeze<br />
              <span className="text-emerald-400">before they hit your account.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
              A card drips ninety days of unused subscriptions. Our Freeze Shield catches every drop, converts the red flow into emerald evidence, and locks the amounts in one clean audit block.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest">
              <Droplet size={12} /> Avg $284/mo recovered
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ FX ============================ */
function FXStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const portalOp = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0.2, 1, 0.2]);

  const aX = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [-260, 0, 260]);
  const aRot = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [-18, 0, 18]);
  const aSymOp = useTransform(scrollYProgress, [0.4, 0.5], [1, 0]);
  const aUsdOp = useTransform(scrollYProgress, [0.5, 0.6], [0, 1]);
  const aScale = useTransform(scrollYProgress, [0.45, 0.55], [1, 1.08]);
  const bX = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [-140, 0, 140]);
  const bY = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [40, 0, -40]);
  const bRot = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [-6, 0, 6]);
  const bSymOp = useTransform(scrollYProgress, [0.42, 0.52], [1, 0]);
  const bUsdOp = useTransform(scrollYProgress, [0.52, 0.62], [0, 1]);
  const cX = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [-40, 0, -220]);
  const cRot = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [10, 0, -14]);
  const cSymOp = useTransform(scrollYProgress, [0.44, 0.54], [1, 0]);
  const cUsdOp = useTransform(scrollYProgress, [0.54, 0.64], [0, 1]);

  const cards = [
    { sym: "€", code: "EUR", amt: "€120.00", usd: "$130.20", from: "Paris HQ", x: aX, rot: aRot, y: null, scale: aScale, so: aSymOp, uo: aUsdOp },
    { sym: "¥", code: "JPY", amt: "¥18,400", usd: "$122.10", from: "Tokyo", x: bX, rot: bRot, y: bY, scale: null, so: bSymOp, uo: bUsdOp },
    { sym: "£", code: "GBP", amt: "£82.40", usd: "$104.60", from: "London", x: cX, rot: cRot, y: null, scale: null, so: cSymOp, uo: cUsdOp },
  ];

  return (
    <section ref={ref} className="relative min-h-[180vh]" style={{ background: "#0f172a" }} data-testid="home-fx">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="max-w-6xl mx-auto px-8 w-full">
          <div className="text-center mb-8">
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3 flex items-center justify-center gap-2">
              <Globe size={12} /> Stage 05 · FX Portal
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Foreign receipts in.<br />
              <span className="text-emerald-400">Live USD out.</span>
            </h2>
          </div>

          <div className="relative h-[420px]" style={{ perspective: 1200 }}>
            <motion.div
              style={{ opacity: portalOp, willChange: "opacity" }}
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-24 pointer-events-none"
            >
              <div className="absolute inset-x-0 top-1/2 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(16,185,129,1), transparent)", boxShadow: "0 0 24px rgba(16,185,129,0.9)" }} />
              <div className="absolute inset-0"
                style={{
                  backgroundImage: "repeating-linear-gradient(90deg, rgba(16,185,129,0.25) 0 1px, transparent 1px 40px)",
                  maskImage: "linear-gradient(90deg, transparent, black 30%, black 70%, transparent)",
                  WebkitMaskImage: "linear-gradient(90deg, transparent, black 30%, black 70%, transparent)",
                }} />
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-full bg-slate-950/80 border border-emerald-500/40 text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                LIVE FX PORTAL · USD
              </div>
            </motion.div>

            <div className="absolute inset-0 flex items-center justify-center">
              {cards.map((c, i) => (
                <motion.div
                  key={c.code}
                  data-testid={`fx-card-${c.code}`}
                  style={{
                    x: c.x, y: c.y || 0, rotate: c.rot, scale: c.scale || 1,
                    transformStyle: "preserve-3d", willChange: "transform",
                    background: "linear-gradient(140deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))",
                    boxShadow: "0 30px 80px -20px rgba(16,185,129,0.45), 0 0 0 1px rgba(16,185,129,0.25)",
                    zIndex: 10 - i,
                  }}
                  className="absolute w-56 rounded-2xl p-5 border border-emerald-500/25 backdrop-blur"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">{c.from}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{c.code}</div>
                  </div>
                  <div className="mt-6 relative h-10">
                    <motion.div style={{ opacity: c.so, willChange: "opacity" }} className="absolute inset-0 flex items-baseline gap-1">
                      <span className="text-3xl font-mono font-bold text-white">{c.sym}</span>
                      <span className="text-xl font-mono font-bold text-white">{c.amt.slice(1)}</span>
                    </motion.div>
                    <motion.div style={{ opacity: c.uo, willChange: "opacity" }} className="absolute inset-0 flex items-baseline gap-1">
                      <span className="text-3xl font-mono font-bold text-emerald-400">$</span>
                      <span className="text-xl font-mono font-bold text-emerald-400">{c.usd.slice(1)}</span>
                    </motion.div>
                  </div>
                  <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-slate-500">Live rate applied · sealed</div>
                </motion.div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-slate-300 max-w-2xl mx-auto text-center leading-relaxed">
            Every foreign-currency receipt passes through the FX portal and emerges as a signed USD equivalent, rate-stamped for audit.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================ AGENT ============================ */
function AgentStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  const COMMAND = 'sheet-ai --export "Q1 receipts" --format pdf --deductibles-only';
  const [typed, setTyped] = useState("");
  useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => {
      const p = Math.max(0, Math.min(1, (v - 0.1) / 0.35));
      const cut = Math.floor(p * COMMAND.length);
      setTyped(COMMAND.slice(0, cut));
    });
    return () => unsub();
  }, [scrollYProgress]);

  const p0x = useTransform(scrollYProgress, [0.55, 0.9], [0, -180]);
  const p0r = useTransform(scrollYProgress, [0.55, 0.9], [0, -14]);
  const p0o = useTransform(scrollYProgress, [0.5, 0.65], [0, 1]);
  const p1x = useTransform(scrollYProgress, [0.55, 0.9], [0, -90]);
  const p1r = useTransform(scrollYProgress, [0.55, 0.9], [0, -6]);
  const p1o = useTransform(scrollYProgress, [0.52, 0.68], [0, 1]);
  const p2o = useTransform(scrollYProgress, [0.54, 0.7], [0, 1]);
  const p3x = useTransform(scrollYProgress, [0.55, 0.9], [0, 90]);
  const p3r = useTransform(scrollYProgress, [0.55, 0.9], [0, 6]);
  const p3o = useTransform(scrollYProgress, [0.56, 0.72], [0, 1]);
  const p4x = useTransform(scrollYProgress, [0.55, 0.9], [0, 180]);
  const p4r = useTransform(scrollYProgress, [0.55, 0.9], [0, 14]);
  const p4o = useTransform(scrollYProgress, [0.58, 0.74], [0, 1]);

  const pages = [
    { x: p0x, r: p0r, o: p0o, label: "Q1 · Meals" },
    { x: p1x, r: p1r, o: p1o, label: "Q1 · Travel" },
    { x: null, r: null, o: p2o, label: "Q1 · Summary" },
    { x: p3x, r: p3r, o: p3o, label: "Q1 · Software" },
    { x: p4x, r: p4r, o: p4o, label: "Q1 · Deductions" },
  ];

  const promptDone = typed.length >= COMMAND.length;

  return (
    <section ref={ref} className="relative min-h-[200vh]" style={{ background: "#0f172a" }} data-testid="home-agent">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="max-w-6xl mx-auto px-8 w-full">
          <div className="text-center mb-8">
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3 flex items-center justify-center gap-2">
              <Terminal size={12} /> Stage 06 · Sheet AI Agent
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Type it. <span className="text-emerald-400">Get it.</span>
            </h2>
          </div>

          <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden"
            style={{ background: "rgba(2,6,23,0.85)", border: "1px solid rgba(16,185,129,0.35)", boxShadow: "0 30px 80px -20px rgba(16,185,129,0.4)" }}
            data-testid="agent-terminal"
          >
            <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              <span className="ml-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">sheet-ai · secure shell</span>
            </div>
            <div className="p-5 font-mono text-sm min-h-[110px]">
              <div className="text-emerald-400">$ <span className="text-slate-200">{typed}</span>
                <span className={`inline-block w-2 h-4 ml-0.5 align-middle bg-emerald-400 ${promptDone ? "opacity-0" : "animate-pulse"}`} />
              </div>
              {promptDone && (
                <div className="mt-3 text-slate-400">
                  <span className="text-emerald-400">✓</span> Building PDF pages… <span className="text-emerald-300">5 documents</span> · signed · watermarked
                </div>
              )}
            </div>
          </div>

          <div className="relative h-[280px] mt-10" style={{ perspective: 1400 }}>
            <div className="absolute inset-0 flex items-center justify-center">
              {pages.map((p, i) => (
                <motion.div
                  key={i}
                  data-testid={`agent-pdf-${i}`}
                  style={{
                    x: p.x || 0, rotate: p.r || 0, opacity: p.o,
                    transformStyle: "preserve-3d", willChange: "transform",
                    zIndex: i === 2 ? 10 : 5,
                    boxShadow: "0 20px 60px -15px rgba(16,185,129,0.4), 0 0 0 1px rgba(16,185,129,0.2)",
                  }}
                  className="absolute w-44 h-56 rounded-xl bg-slate-100 flex flex-col p-3"
                >
                  <div className="flex items-center gap-1 pb-2 border-b border-slate-300">
                    <FileText size={12} className="text-emerald-600" />
                    <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Receipt Sheets · PDF</span>
                  </div>
                  <div className="text-[10px] font-semibold text-slate-800 mt-2 tracking-tight">{p.label}</div>
                  <div className="mt-2 space-y-1 flex-1">
                    {Array.from({ length: 5 }).map((_, r) => (
                      <div key={r} className="flex justify-between items-center">
                        <div className="h-1.5 flex-1 mr-2 rounded-full bg-slate-300" />
                        <div className="h-1.5 w-8 rounded-full bg-emerald-500" />
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-slate-300 flex justify-between">
                    <span className="text-[9px] font-mono text-slate-500">2026</span>
                    <span className="text-[10px] font-mono font-bold text-emerald-700">${((i + 1) * 214.45).toFixed(2)}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ============================ COMPLIANCE ============================ */
function ComplianceStage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  // Receipt tilts through 3D space
  const rTilt = useTransform(scrollYProgress, [0, 1], [12, -12]);
  const rY = useTransform(scrollYProgress, [0, 0.4], [40, 0]);
  const rScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 1.02]);

  // Red grid opacity — cross-hatch scanning overlay
  const gridOp = useTransform(scrollYProgress, [0.2, 0.5, 0.85], [0, 0.7, 0.4]);

  // Line-item highlight (the offending one)
  const lineHiOp = useTransform(scrollYProgress, [0.45, 0.55], [0, 1]);
  const lineStrike = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);

  // Alert pop
  const alertOp = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);
  const alertScale = useTransform(scrollYProgress, [0.55, 0.6, 0.7], [0.6, 1.15, 1]);
  const alertY = useTransform(scrollYProgress, [0.55, 0.65], [16, 0]);

  const ITEMS = [
    { desc: "Client dinner — steakhouse", price: "$182.44", over: true },
    { desc: "Uber to venue", price: "$18.60" },
    { desc: "Bar tab", price: "$46.10" },
    { desc: "Coat check", price: "$8.00" },
  ];

  return (
    <section ref={ref} className="relative min-h-[200vh]" style={{ background: "#0f172a" }} data-testid="home-compliance">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="max-w-6xl mx-auto px-8 grid lg:grid-cols-2 gap-16 items-center w-full">
          {/* Left: 3D receipt with red grid + alert */}
          <div className="relative mx-auto w-[380px] h-[500px]" style={{ perspective: 1400 }}>
            <motion.div
              style={{
                y: rY, scale: rScale, rotateY: rTilt,
                transformStyle: "preserve-3d", willChange: "transform",
                background: "linear-gradient(150deg, rgba(15,23,42,0.9), rgba(15,23,42,0.6))",
                border: "1px solid rgba(239,68,68,0.35)",
                boxShadow: "0 30px 80px -20px rgba(239,68,68,0.4), 0 0 0 1px rgba(239,68,68,0.2)",
              }}
              className="absolute inset-x-4 top-8 rounded-3xl p-6 overflow-hidden"
            >
              <div className="flex items-center justify-between pb-3 border-b border-dashed border-white/10">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-red-300">Compliance scan</div>
                  <div className="mt-0.5 font-bold text-white tracking-tight">Steakhouse Prime</div>
                </div>
                <ShieldAlert size={22} className="text-red-400" />
              </div>
              <div className="mt-4 space-y-3 text-sm">
                {ITEMS.map((it, i) => (
                  <div key={i} className="relative flex justify-between font-mono">
                    <span className="text-slate-300">{it.desc}</span>
                    <span className={`font-mono ${it.over ? "text-red-300 font-bold" : "text-white"}`}>{it.price}</span>
                    {it.over && (
                      <>
                        <motion.div
                          style={{ opacity: lineHiOp, willChange: "opacity" }}
                          className="absolute -inset-x-2 -inset-y-1 rounded-md bg-red-500/15 border border-red-500/40"
                        />
                        <motion.div
                          style={{ scaleX: lineStrike, willChange: "transform", transformOrigin: "left" }}
                          className="absolute left-0 right-0 top-1/2 h-px bg-red-400"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-3 border-t border-dashed border-white/10 flex justify-between">
                <span className="text-slate-400 text-xs uppercase tracking-widest">Total</span>
                <span className="font-mono text-2xl font-bold text-red-300">$255.14</span>
              </div>

              {/* Neon-red diagonal grid overlay */}
              <motion.div
                style={{ opacity: gridOp, willChange: "opacity" }}
                className="absolute inset-0 pointer-events-none"
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(239,68,68,0.18) 0 1px, transparent 1px 12px), repeating-linear-gradient(-45deg, rgba(239,68,68,0.18) 0 1px, transparent 1px 12px)",
                    maskImage: "radial-gradient(ellipse at center, black 30%, transparent 90%)",
                    WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 90%)",
                  }}
                />
                <div
                  className="absolute inset-x-0 top-1/2 h-8 -translate-y-1/2"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent, rgba(239,68,68,0.15), transparent)",
                  }}
                />
              </motion.div>
            </motion.div>

            {/* Alert popup */}
            <motion.div
              data-testid="compliance-alert"
              style={{
                opacity: alertOp, scale: alertScale, y: alertY,
                willChange: "transform, opacity",
                boxShadow: "0 0 0 1px rgba(239,68,68,0.55), 0 20px 40px -12px rgba(239,68,68,0.4), 0 0 24px rgba(239,68,68,0.35)",
              }}
              className="absolute -right-4 top-24 w-64 rounded-2xl p-4 bg-slate-950/95 border border-red-500/40 backdrop-blur"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-red-300 mb-1">Policy · Meals cap $50</div>
                  <div className="text-sm font-semibold text-white leading-snug">
                    Line item blocked — $182.44 exceeds cap by <span className="text-red-300 font-mono">$132.44</span>.
                  </div>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    Sent to approver · 2 min ago
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right copy */}
          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3">Stage 07 · Compliance Shield</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              Over-budget items{" "}
              <span className="text-red-400">never make it</span>{" "}
              past the shield.
            </h2>
            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
              The Corporate Policy Enforcer runs a real-time compliance scan the
              instant a receipt lands. Anything above your per-category cap gets
              flagged, striked and routed to an approver — before it ever hits
              your general ledger.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Meals · $50", "Travel · $500", "Software · $200", "Ent. · $75"].map((c) => (
                <span key={c} className="text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded bg-white/5 border border-white/10 text-slate-300 font-mono">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ CAROUSEL ============================ */
function CarouselStage() {
  // stacked → fanned → binder → stacked
  const [state, setState] = useState("stacked");

  const CARDS = [
    { label: "Q1 · Meals",       total: "$1,204.55" },
    { label: "Q1 · Travel",      total: "$4,819.02" },
    { label: "Q1 · Software",    total: "$847.44" },
    { label: "Q1 · Deductions",  total: "$2,847.13" },
    { label: "Q1 · Summary",     total: "$9,718.14" },
    { label: "Q1 · Mileage",     total: "$618.00" },
    { label: "Q1 · Compliance",  total: "$0.00" },
  ];

  const N = CARDS.length;
  const radius = 260;

  const stackVariant = (i) => ({
    x: 0, y: i * -4, rotate: (i - N / 2) * 0.6, scale: 1 - i * 0.02,
    opacity: 1, zIndex: N - i,
  });
  const fanVariant = (i) => {
    const angle = (-Math.PI / 2) + (i / (N - 1) - 0.5) * (Math.PI * 0.9);
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius * 0.55,
      rotate: (angle * 180) / Math.PI + 90,
      scale: 1,
      opacity: 1,
      zIndex: i,
    };
  };
  const binderVariant = { x: 0, y: 0, rotate: 0, scale: 0.05, opacity: 0 };

  const advance = () => {
    if (state === "stacked") setState("fanned");
    else if (state === "fanned") setState("binder");
    else setState("stacked");
  };

  const buttonLabel =
    state === "stacked" ? "Fan out documents" :
    state === "fanned" ? "Compress to binder" :
    "Re-open stack";

  return (
    <section className="relative min-h-[100vh]" style={{ background: "#0f172a" }} data-testid="home-carousel">
      <div className="max-w-6xl mx-auto px-8 py-24 w-full">
        <div className="text-center mb-8">
          <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-3 flex items-center justify-center gap-2">
            <FolderArchive size={12} /> Stage 08 · Export Carousel
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            One tap fans your archive.<br />
            <span className="text-emerald-400">Another seals it shut.</span>
          </h2>
        </div>

        {/* Stage area */}
        <div
          className="relative h-[420px] flex items-center justify-center"
          style={{ perspective: 1400 }}
        >
          {/* Cards */}
          {CARDS.map((c, i) => {
            const v =
              state === "stacked" ? stackVariant(i) :
              state === "fanned"  ? fanVariant(i) :
              binderVariant;
            return (
              <motion.div
                key={i}
                data-testid={`carousel-card-${i}`}
                animate={v}
                transition={{
                  type: "spring", stiffness: 120, damping: 18, mass: 0.7,
                  delay: state === "fanned" ? i * 0.04 : (state === "binder" ? (N - i) * 0.03 : 0),
                }}
                style={{
                  willChange: "transform, opacity",
                  transformStyle: "preserve-3d",
                  boxShadow: "0 20px 60px -15px rgba(16,185,129,0.4), 0 0 0 1px rgba(16,185,129,0.2)",
                }}
                className="absolute w-44 h-56 rounded-xl bg-slate-100 flex flex-col p-3"
              >
                <div className="flex items-center gap-1 pb-2 border-b border-slate-300">
                  <FileText size={12} className="text-emerald-600" />
                  <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                    Receipt Sheets · PDF
                  </span>
                </div>
                <div className="text-[10px] font-semibold text-slate-800 mt-2 tracking-tight">{c.label}</div>
                <div className="mt-2 space-y-1 flex-1">
                  {Array.from({ length: 5 }).map((_, r) => (
                    <div key={r} className="flex justify-between items-center">
                      <div className="h-1.5 flex-1 mr-2 rounded-full bg-slate-300" />
                      <div className="h-1.5 w-8 rounded-full bg-emerald-500" />
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-slate-300 flex justify-between">
                  <span className="text-[9px] font-mono text-slate-500">2026</span>
                  <span className="text-[10px] font-mono font-bold text-emerald-700">{c.total}</span>
                </div>
              </motion.div>
            );
          })}

          {/* Binder — only visible when state === 'binder' */}
          <motion.div
            data-testid="carousel-binder"
            animate={{
              scale: state === "binder" ? 1 : 0.2,
              opacity: state === "binder" ? 1 : 0,
              rotate: state === "binder" ? 0 : -20,
            }}
            transition={{ type: "spring", stiffness: 140, damping: 16, delay: state === "binder" ? 0.35 : 0 }}
            style={{ willChange: "transform, opacity" }}
            className="absolute w-40 h-52 rounded-2xl flex flex-col items-center justify-center gap-3"
          >
            <div
              className="w-full h-full rounded-2xl flex flex-col items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #064e3b 0%, #10b981 100%)",
                boxShadow: "0 30px 80px -20px rgba(16,185,129,0.6), 0 0 0 1px rgba(16,185,129,0.4), inset 0 0 40px rgba(255,255,255,0.06)",
              }}
            >
              <FolderArchive size={36} className="text-slate-950" strokeWidth={2.4} />
              <div className="text-slate-950 font-bold text-sm tracking-tight">Q1 · Sealed</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-800">{N} docs · signed</div>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex flex-col items-center gap-3">
          <button
            onClick={advance}
            data-testid="carousel-cta"
            className="btn-neon h-14 px-8 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base flex items-center gap-2"
          >
            {buttonLabel} <ArrowRight size={18} />
          </button>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
            State · {state}
          </div>
        </div>
      </div>
    </section>
  );
}


/* ============================ FINALE ============================ */
function FinaleStage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cta = () => {
    if (!user) navigate("/login");
    else if (user.subscription_status !== "active" && user.subscription_status !== "active_business") navigate("/paywall");
    else navigate("/dashboard");
  };
  return (
    <section
      className="relative min-h-[70vh] flex items-center justify-center"
      style={{ background: "#0f172a" }}
      data-testid="home-finale"
    >
      <div className="max-w-3xl px-8 text-center">
        <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-4 flex items-center justify-center gap-2">
          <Building2 size={12} /> Ready when you are
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.02]">
          Reclaim your{" "}
          <span className="text-emerald-400">write-offs.</span>
        </h2>
        <p className="mt-5 text-slate-300 text-lg max-w-xl mx-auto">
          Pick a plan, sign in with Google, and scan your first receipt in
          under thirty seconds.
        </p>
        <button
          onClick={cta}
          data-testid="home-finale-cta"
          className="btn-neon mt-8 h-14 px-8 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base inline-flex items-center gap-2"
        >
          Choose your plan <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
}

/* ============================ PAGE ============================ */
export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-slate-100 overflow-x-clip relative" style={{ background: "#0f172a" }}>
      {/* Top nav */}
      <header
        className="fixed top-0 inset-x-0 z-40"
        style={{
          background: "rgba(15,23,42,0.6)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between">
          <Logo size="md" />
          <nav className="flex items-center gap-4">
            <button
              onClick={() => navigate("/login")}
              data-testid="home-login-btn"
              className="text-slate-300 hover:text-white text-sm font-medium"
            >
              Sign in
            </button>
            <button
              onClick={() => navigate("/paywall")}
              data-testid="home-nav-cta"
              className="btn-neon h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm"
            >
              Get started
            </button>
          </nav>
        </div>
      </header>

      <div className="pt-16 relative">
        <HeroStage />
        <ScanStage />
        <VaultStage />
        <LeakStage />
        <FXStage />
        <AgentStage />
        <ComplianceStage />
        <CarouselStage />
        <FinaleStage />
      </div>

      <footer className="border-t border-white/5 py-8 px-8 max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4" data-testid="landing-footer">
        <Logo size="sm" />
        <nav className="flex items-center gap-5 text-xs font-mono uppercase tracking-widest text-slate-500">
          <a href="/privacy" data-testid="footer-link-privacy" className="hover:text-emerald-300 transition-colors">Privacy</a>
          <span className="text-slate-700">·</span>
          <a href="/terms" data-testid="footer-link-terms" className="hover:text-emerald-300 transition-colors">Terms</a>
          <span className="text-slate-700">·</span>
          <a href="/status" data-testid="footer-link-status" className="hover:text-emerald-300 transition-colors">Status</a>
        </nav>
        <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">
          © 2026 Receipt Sheets · SOC 2 · 256-bit AES · GDPR
        </div>
      </footer>
    </div>
  );
}
